import { formatDollars } from '@fruitful/fruitils';
import type { AccountSummary } from '@fruitful/types';
import { BillActivationStatus, Provider } from '@fruitful/types';
import { Badge } from 'primereact/badge';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    HolisticAccountLogo,
    Icon,
    UserCheckIcon,
    XStack,
    YStack,
} from 'smoothie';

import { formatToSimpleDateString } from '../../../common/utils/formatting';
import type { Bill } from '../../entityTypes';
import type { TransactionSearchResult } from '../hooks/useSearchTransactions';

const ProviderIcon = ({ provider }: { provider: Provider }) => {
    if (provider === Provider.MANUAL) {
        return <Icon color="$dropletBlue1100" icon={UserCheckIcon} size={20} />;
    } else if (provider === Provider.QUILTT) {
        return (
            <img
                src="https://d1pwidzl9kib4u.cloudfront.net/grove-icons/quiltt_logo.png"
                alt="Quiltt"
                style={{
                    width: 20,
                    height: 20,
                    objectFit: 'contain',
                }}
            />
        );
    } else if (
        provider === Provider.SYNCTERA ||
        provider === Provider.APEX ||
        provider === Provider.ORION
    ) {
        return (
            <div style={{ width: 20, height: 20 }}>
                <HolisticAccountLogo
                    institutionName="Fruitful"
                    size="sm"
                    provider={provider}
                />
            </div>
        );
    } else if (provider === Provider.SPINWHEEL) {
        return (
            <img
                src="https://d1pwidzl9kib4u.cloudfront.net/grove-icons/Spinwheel-Icon-RGB.png"
                alt="Spinwheel"
                style={{
                    width: 20,
                    height: 20,
                    objectFit: 'contain',
                }}
            />
        );
    }

    return null;
};

type SortOption = 'relevance' | 'date-newest' | 'date-oldest' | 'amount';

interface TransactionSearchResultsProps {
    transactions: TransactionSearchResult[];
    query: string;
    accounts: AccountSummary[];
    sortBy: SortOption;
    bills: Bill[];
}

const findAccountById = (
    accountId: string,
    accounts: AccountSummary[],
): AccountSummary | undefined => {
    // First try direct ID match
    let account = accounts.find((account) => account.id === accountId);

    if (account != null) {
        return account;
    }

    // For Quiltt accounts, the transaction accountId is the Quiltt account ID (starts with "acct_")
    // The account.id might be the Quiltt account ID, or we might need to check other fields
    if (accountId.startsWith('acct_')) {
        // Try to find Quiltt accounts where the id matches (account.id might be the Quiltt account ID)
        account = accounts.find(
            (account) =>
                account.provider === Provider.QUILTT &&
                account.id === accountId,
        );

        if (account != null) {
            return account;
        }

        // Also check accounts that might have quilttAccountId as an extra field
        // (AccountSummary can have extra fields added dynamically)
        const accountWithExtras = accounts.find((account) => {
            const accountWithQuilttId = account as AccountSummary & {
                quilttAccountId?: string;
                quilttAccount?: { id: string };
            };
            return (
                account.provider === Provider.QUILTT &&
                (accountWithQuilttId.quilttAccountId === accountId ||
                    accountWithQuilttId.quilttAccount?.id === accountId)
            );
        });

        if (accountWithExtras != null) {
            return accountWithExtras;
        }
    }

    return undefined;
};

const findAccountForTransaction = (
    transaction: TransactionSearchResult,
    accounts: AccountSummary[],
): AccountSummary | undefined => {
    // Try localAccountId first (internal account ID)
    if (transaction.localAccountId != null) {
        const account = findAccountById(transaction.localAccountId, accounts);
        if (account != null) {
            return account;
        }
    }

    // If localAccountId doesn't match, try accountId (Quiltt account ID for Quiltt transactions)
    if (transaction.accountId != null) {
        const account = findAccountById(transaction.accountId, accounts);
        if (account != null) {
            return account;
        }
    }

    return undefined;
};

const findMatchingActiveBill = (
    transaction: TransactionSearchResult,
    bills: Bill[],
): Bill | undefined => {
    // Only check active bills
    const activeBills = bills.filter(
        (bill) => bill.activationStatus === BillActivationStatus.ACTIVE,
    );

    // Check if this transaction is in any bill's transactions array
    // Match by transactionId or _id
    const transactionId = transaction.transactionId ?? transaction._id;

    for (const bill of activeBills) {
        if (bill.transactions != null && bill.transactions.length > 0) {
            const matchingTransaction = bill.transactions.find(
                (billTransaction) =>
                    billTransaction.transactionId === transactionId ||
                    billTransaction.transactionId === transaction._id,
            );
            if (matchingTransaction != null) {
                return bill;
            }
        }
    }

    return undefined;
};

const sortTransactions = (
    transactions: TransactionSearchResult[],
    sortBy: SortOption,
): TransactionSearchResult[] => {
    const sorted = [...transactions]; // Create a copy to avoid mutating the original

    switch (sortBy) {
        case 'relevance':
            // Keep original order (sorted by score from backend)
            return sorted;
        case 'date-newest':
            return sorted.sort((a, b) => {
                const dateA = new Date(a.transactionDate).getTime();
                const dateB = new Date(b.transactionDate).getTime();
                return dateB - dateA; // Newest first
            });
        case 'date-oldest':
            return sorted.sort((a, b) => {
                const dateA = new Date(a.transactionDate).getTime();
                const dateB = new Date(b.transactionDate).getTime();
                return dateA - dateB; // Oldest first
            });
        case 'amount':
            return sorted.sort((a, b) => {
                // Sort by absolute amount, largest first
                return Math.abs(b.amount) - Math.abs(a.amount);
            });
        default:
            return sorted;
    }
};

export const TransactionSearchResults = ({
    transactions,
    query,
    accounts,
    sortBy,
    bills,
}: TransactionSearchResultsProps) => {
    if (transactions.length === 0) {
        return (
            <BodyMediumRegular>
                No transactions found for &ldquo;{query}&rdquo;
            </BodyMediumRegular>
        );
    }

    const sortedTransactions = sortTransactions(transactions, sortBy);

    const transactionsWithAccounts = sortedTransactions
        .map((transaction) => {
            const account = findAccountForTransaction(transaction, accounts);
            const matchingBill = findMatchingActiveBill(transaction, bills);
            return { transaction, account, matchingBill };
        })
        .filter(({ account }) => account != null); // Only show transactions with matching accounts

    if (transactionsWithAccounts.length === 0) {
        return (
            <BodyMediumRegular>
                No transactions found for &ldquo;{query}&rdquo;
            </BodyMediumRegular>
        );
    }

    return (
        <YStack gap={16}>
            <BodyMediumEmphasized>
                Found {transactionsWithAccounts.length} transaction
                {transactionsWithAccounts.length !== 1 ? 's' : ''} for &ldquo;
                {query}&rdquo;
            </BodyMediumEmphasized>
            <YStack gap={12}>
                {transactionsWithAccounts.map(
                    ({ transaction, account, matchingBill }) => {
                        return (
                            <TransactionRow
                                key={transaction._id}
                                transaction={transaction}
                                account={account}
                                matchingBill={matchingBill}
                            />
                        );
                    },
                )}
            </YStack>
        </YStack>
    );
};

const TransactionRow = ({
    transaction,
    account,
    matchingBill,
}: {
    transaction: TransactionSearchResult;
    account?: AccountSummary;
    matchingBill?: Bill;
}) => {
    const formattedDate = formatToSimpleDateString(
        transaction.transactionDate,
        {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        },
    );

    const amountPrefix = transaction.type === 'debit' ? '-' : '+';
    const formattedAmount = `${amountPrefix}${formatDollars(
        Math.abs(transaction.amount),
    )}`;

    // Determine provider from transaction aggregatorData or account
    const provider =
        account?.provider ??
        (transaction.aggregatorData?.aggregatorName === 'quiltt'
            ? Provider.QUILTT
            : undefined);

    return (
        <XStack
            padding={16}
            borderRadius={8}
            backgroundColor="$gray100"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={16}
        >
            <XStack flex={1} gap={12} alignItems="flex-start">
                {/* Provider logo at top left */}
                {provider != null && (
                    <YStack paddingTop={2}>
                        <ProviderIcon provider={provider} />
                    </YStack>
                )}
                <YStack flex={1} gap={4}>
                    {/* Transaction ID at top */}
                    {transaction.transactionId != null &&
                        transaction.transactionId !== '' && (
                            <BodySmallRegular color="$gray800" fontSize="$1">
                                {transaction.transactionId}
                            </BodySmallRegular>
                        )}
                    <XStack gap={8} alignItems="center">
                        <BodyMediumEmphasized>
                            {transaction.description}
                        </BodyMediumEmphasized>
                        {matchingBill != null && (
                            <Badge
                                value="Active Bill"
                                severity="success"
                                style={{
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    height: '18px',
                                }}
                            />
                        )}
                    </XStack>
                    {/* Account information */}
                    {account != null ? (
                        <YStack gap={2} marginTop={4}>
                            <BodySmallRegular color="$gray1000">
                                {account.institutionName}
                            </BodySmallRegular>
                            <XStack gap={4} alignItems="center">
                                <BodySmallRegular color="$gray1000">
                                    {account.name}
                                </BodySmallRegular>
                                {account.accountNumberMasked != null &&
                                    account.accountNumberMasked !== '' && (
                                        <BodySmallRegular color="$gray1000">
                                            *{account.accountNumberMasked}
                                        </BodySmallRegular>
                                    )}
                            </XStack>
                        </YStack>
                    ) : (
                        // Show provider info even if account not found
                        provider != null && (
                            <BodySmallRegular color="$gray1000" marginTop={4}>
                                Provider: {provider}
                            </BodySmallRegular>
                        )
                    )}
                    {transaction.llmVendor != null &&
                        transaction.llmVendor !== '' && (
                            <BodySmallRegular color="$gray1000">
                                Vendor: {transaction.llmVendor}
                            </BodySmallRegular>
                        )}
                    {transaction.llmCategory != null &&
                        transaction.llmCategory !== '' && (
                            <BodySmallRegular color="$gray1000">
                                Category:{' '}
                                {transaction.llmCategory
                                    .replace(/_/g, ' ')
                                    .replace(/spending /i, '')}
                            </BodySmallRegular>
                        )}
                </YStack>
            </XStack>
            <YStack alignItems="flex-end" gap={4}>
                <BodyMediumEmphasized
                    color={
                        transaction.type === 'debit' ? '$red1000' : '$green1000'
                    }
                >
                    {formattedAmount}
                </BodyMediumEmphasized>
                <BodySmallRegular color="$gray1000">
                    {formattedDate}
                </BodySmallRegular>
            </YStack>
        </XStack>
    );
};
